/**
 * Toasts (shadcn/ui's sonner wrapper, adapted). Mount one `<Toaster theme=…/>`
 * near the root; raise toasts with `toast()` from `sonner` in a container —
 * the theme is a prop, not read from a store, so this stays presentational.
 */
import type { CSSProperties } from 'react';
import { Toaster as Sonner, type ToasterProps } from 'sonner';

/**
 * sonner paints toasts from its own CSS variables, not from classes — point
 * them at the tokens, or a toast renders in sonner's black/white regardless of
 * the palette. (A CSS custom property is a valid `style` key; the type says so
 * with a template-literal index rather than an assertion.)
 */
const TOKEN_STYLE: CSSProperties & Record<`--${string}`, string> = {
  '--normal-bg': 'var(--popover)',
  '--normal-text': 'var(--popover-foreground)',
  '--normal-border': 'var(--border)',
  '--success-bg': 'var(--popover)',
  '--success-text': 'var(--popover-foreground)',
  '--success-border': 'var(--success)',
  '--error-bg': 'var(--popover)',
  '--error-text': 'var(--popover-foreground)',
  '--error-border': 'var(--destructive)',
  '--warning-bg': 'var(--popover)',
  '--warning-text': 'var(--popover-foreground)',
  '--warning-border': 'var(--warning)',
};

export function Toaster({ theme, ...props }: ToasterProps) {
  return (
    <Sonner
      theme={theme ?? 'dark'}
      className="toaster group"
      style={TOKEN_STYLE}
      toastOptions={{
        classNames: {
          toast: 'group toast bg-popover text-popover-foreground border-border shadow-lg',
          description: 'text-muted-foreground',
          actionButton: 'bg-primary text-primary-foreground',
          cancelButton: 'bg-muted text-muted-foreground',
          error: 'border-destructive',
          success: 'border-success',
          warning: 'border-warning',
        },
      }}
      {...props}
    />
  );
}
